import { createClient, SupabaseClient, RealtimeChannel } from '@supabase/supabase-js';
import { config } from '../config';

// Database types - these would typically be generated from your Supabase schema
export interface Database {
  public: {
    Tables: {
      users: {
        Row: {
          id: string;
          email: string;
          full_name: string;
          role: string;
          avatar_url: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['users']['Row'], 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Database['public']['Tables']['users']['Insert']>;
      };
      orders: {
        Row: {
          id: string;
          order_number: string;
          ship_id: string;
          ship_name: string;
          status: string;
          priority: string;
          requested_delivery_date: string;
          total_items: number;
          total_value: number;
          notes: string | null;
          created_by: string;
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['orders']['Row'], 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Database['public']['Tables']['orders']['Insert']>;
      };
      order_items: {
        Row: {
          id: string;
          order_id: string;
          product_id: string;
          quantity: number;
          unit_price: number;
          picked_quantity: number;
          lot_number: string | null;
          created_at: string;
        };
        Insert: Omit<Database['public']['Tables']['order_items']['Row'], 'id' | 'created_at'>;
        Update: Partial<Database['public']['Tables']['order_items']['Insert']>;
      };
      products: {
        Row: {
          id: string;
          sku: string;
          name: string;
          description: string | null;
          category: string;
          unit: string;
          unit_price: number;
          allergens: string[];
          storage_zone: string;
          min_stock: number;
          max_stock: number;
          current_stock: number;
          is_active: boolean;
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['products']['Row'], 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Database['public']['Tables']['products']['Insert']>;
      };
      inventory_lots: {
        Row: {
          id: string;
          product_id: string;
          lot_number: string;
          quantity: number;
          received_date: string;
          expiry_date: string;
          supplier: string;
          location_id: string;
          created_at: string;
        };
        Insert: Omit<Database['public']['Tables']['inventory_lots']['Row'], 'id' | 'created_at'>;
        Update: Partial<Database['public']['Tables']['inventory_lots']['Insert']>;
      };
      warehouse_locations: {
        Row: {
          id: string;
          code: string;
          zone: string;
          aisle: string;
          rack: string;
          shelf: string;
          capacity: number;
          current_usage: number;
          is_active: boolean;
          created_at: string;
        };
        Insert: Omit<Database['public']['Tables']['warehouse_locations']['Row'], 'id' | 'created_at'>;
        Update: Partial<Database['public']['Tables']['warehouse_locations']['Insert']>;
      };
      deliveries: {
        Row: {
          id: string;
          order_id: string;
          delivery_number: string;
          status: string;
          scheduled_date: string;
          actual_date: string | null;
          vehicle_id: string | null;
          driver_name: string | null;
          notes: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['deliveries']['Row'], 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Database['public']['Tables']['deliveries']['Insert']>;
      };
    };
  };
}

// Create Supabase client
export const supabase: SupabaseClient<Database> = createClient<Database>(
  config.supabase.url,
  config.supabase.anonKey,
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
    },
    realtime: {
      params: {
        eventsPerSecond: 10,
      },
    },
  }
);

// Helper to create realtime subscriptions
export function subscribeToTable(
  table: string,
  callback: (payload: { eventType: string; new: unknown; old: unknown }) => void
): RealtimeChannel {
  return supabase
    .channel(`public:${table}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table,
      },
      (payload) => {
        callback({
          eventType: payload.eventType,
          new: payload.new,
          old: payload.old,
        });
      }
    )
    .subscribe();
}

// Helper to unsubscribe from a channel
export async function unsubscribeFromChannel(channel: RealtimeChannel): Promise<void> {
  await supabase.removeChannel(channel);
}

export default supabase;
